import { FilterConfig } from 'App/babel/filterConfigs/FilterConfig';
import RequestBuilder, { comparisonTypes } from 'Resources/RequestBuilder';
import _ from 'lodash';
import Competitor from 'Resources/Competitor';
import FieldTranslations from 'Resources/FieldTranslations';
import { generateFilter, FilterPreset, generateStandardAndHiddenFilter } from '../filterGenerators';
import AccessType from 'App/enum/AccessType';
import { AppState } from 'App/babel/store/reducers/AppReducer';
import SalesCoach from 'Resources/SalesCoach';
import moment from 'moment';
import AppService from 'App/legacy/AppService';

const orderFilterConfigs = (appState?: AppState): { [key: string]: FilterConfig } => ({
	// No parent
	...generateStandardAndHiddenFilter(
		'Date',
		FilterPreset.Date,
		{
			field: 'date',
			title: 'default.closeDate2'
		},
		'future',
		appState
	),
	...generateStandardAndHiddenFilter('Currency', FilterPreset.Currency, {
		field: 'currency'
	}),
	ValueRange: {
		title: 'default.value',
		type: 'raw',
		inputType: 'range',
		displayType: 'range',
		toUrl: function (filter) {
			var f = { c: filter.comparisonType, v: filter.value, i: false };
			if (filter.inactive) {
				f.i = true;
			} else if (Array.isArray(filter.value) && !filter.value.length) {
				return undefined;
			}
			return f;
		},
		generate: function () {
			return { value: { start: null, end: null } };
		},
		valueSuffix: function (filterProps) {
			const metaData = AppService.getMetadata();
			var activeFilters = filterProps.activeFilters;
			var suffix = metaData.defaultCurrency.iso.toUpperCase();
			if (
				activeFilters.Currency &&
				activeFilters.Currency.value &&
				Array.isArray(activeFilters.Currency.value) &&
				activeFilters.Currency.value.length
			) {
				suffix = activeFilters.Currency.value[0].toString();
			}
			return suffix;
		},
		build: function (filter, rb, getField, useTags, activeFilters: any) {
			var metadata = AppService.getMetadata();
			var selectedCurrency: typeof metadata.defaultCurrency | undefined;

			if (
				activeFilters &&
				activeFilters.Currency &&
				activeFilters.Currency.value &&
				Array.isArray(activeFilters.Currency.value) &&
				activeFilters.Currency.value.length
			) {
				selectedCurrency = _.find(metadata.customerCurrencies, function (currency) {
					return currency.iso.toLowerCase() === activeFilters.Currency.value[0]?.toLowerCase();
				});
			} else {
				selectedCurrency = metadata.defaultCurrency;
			}

			['start', 'end'].forEach(key => {
				if (filter.value[key] || filter.value[key] === 0) {
					var or = rb.orBuilder();
					or.next();

					var myCurrency = or.groupBuilder();
					myCurrency.addFilter(
						getField('value'),
						key === 'start' ? comparisonTypes.GreaterThanEquals : comparisonTypes.LessThanEquals,
						filter.value[key]
					);
					myCurrency.addFilter(getField('currency'), comparisonTypes.Equals, selectedCurrency?.iso);
					myCurrency.done();

					or.next();

					var otherCurrencies = or.groupBuilder();
					otherCurrencies.addFilter(
						getField('valueInMasterCurrency'),
						key === 'start' ? comparisonTypes.GreaterThanEquals : comparisonTypes.LessThanEquals,
						Math.round(filter.value[key] / (selectedCurrency?.rate || 1))
					);
					otherCurrencies.addFilter(getField('currency'), comparisonTypes.NotEquals, selectedCurrency?.iso);
					otherCurrencies.done();
					or.done();
				}
			});
		}
	},
	...generateStandardAndHiddenFilter(
		'OrderRowProduct',
		FilterPreset.Product,
		{ field: 'orderRow.product.id' },
		'orderRow.product.id'
	),
	OrderRowActiveProduct: generateFilter(
		FilterPreset.Product,
		{
			// This filter is only relevant to certain custom sales boards and shouldn't show up in the normal list
			hide: true,
			dataPromise: () => Tools.ProductTreeFilterMeta(false, '')
		},
		'orderRow.product.id'
	),
	...generateStandardAndHiddenFilter(
		'Stage',
		FilterPreset.Stage,
		{
			field: 'stage.id'
		},
		false
	),
	...generateStandardAndHiddenFilter('Campaign', FilterPreset.Campaign),
	ProbabilityRange: generateFilter(FilterPreset.Range, {
		field: 'probability',
		title: 'default.probability',
		hide: true
	}),
	SubAccounts: {
		field: 'client.operationalAccount.id',
		title: 'account.subaccounts',
		type: 'radio',
		inputType: 'radio',
		options: [
			{ text: 'default.show', inactive: true, comparisonType: 'NotEquals' },
			{ text: 'default.hide', value: null, comparisonType: 'Equals' },
			{ text: 'default.only', value: null, comparisonType: 'NotEquals' }
		]
	},
	...generateStandardAndHiddenFilter('Notes', FilterPreset.Notes),
	...generateStandardAndHiddenFilter(
		'RegDate',
		FilterPreset.Date,
		{
			field: 'regDate',
			title: 'default.regDate'
		},
		'future'
	),
	...generateStandardAndHiddenFilter(
		'User',
		FilterPreset.User,
		{
			field: 'user.id',
			title: 'default.users',
			parent: 'default.user'
		},
		AccessType.ORDER
	),
	...generateStandardAndHiddenFilter('UserRole', FilterPreset.Role, {
		field: 'user.role.id'
	}),
	Role: generateFilter(FilterPreset.Role, { hide: true, field: 'user.role.id' }), // here for compatibility, shouldn't show up in list
	...generateStandardAndHiddenFilter('RegBy', FilterPreset.User, {
		title: 'default.createdBy',
		field: 'regBy.id'
	}),

	// Section Company
	Client: generateFilter(
		FilterPreset.ResourceLazyList,
		{
			title: 'default.account',
			parent: 'default.account',
			hide: true
		},
		'client'
	),
	...generateStandardAndHiddenFilter(
		'Account',
		FilterPreset.ResourceLazyList,
		{
			title: 'default.account',
			parent: 'default.account'
		},
		'client'
	),
	...generateStandardAndHiddenFilter(
		'AccountManager',
		FilterPreset.User,
		{
			field: 'client.user.id',
			title: 'default.accountManagers',
			parent: 'default.account'
		},
		AccessType.ORDER
	),

	...generateStandardAndHiddenFilter('AccountCategory', FilterPreset.ClientCategory, {
		field: 'client.category.id'
	}),
	// Section Order - other
	...generateStandardAndHiddenFilter(
		'Contact',
		FilterPreset.ResourceLazyList,
		{
			parent: 'filter.orderOther'
		},
		'contact'
	),
	...generateStandardAndHiddenFilter('Description', FilterPreset.Text, {
		field: 'description',
		title: 'default.description',
		parent: 'filter.orderOther'
	}),
	...generateStandardAndHiddenFilter('Id', FilterPreset.Text, {
		field: 'id',
		title: 'order.id',
		parent: 'filter.orderOther'
	}),
	...generateStandardAndHiddenFilter(
		'CloseDate',
		FilterPreset.Date,
		{
			field: 'closeDate',
			title: 'default.closeDate',
			parent: 'filter.orderOther'
		},
		'future'
	),
	...generateStandardAndHiddenFilter('SalesCoach', FilterPreset.List, {
		field: 'salesCoach.id',
		title: 'admin.salesProcess',
		parent: 'filter.orderOther',
		dataPromise: async () => {
			const filter = new RequestBuilder();
			filter.addFilter({ field: 'active' }, comparisonTypes.Equals, true);
			const { data } = await SalesCoach.find(filter.build());
			return { data };
		}
	}),
	...generateStandardAndHiddenFilter('LostReason', FilterPreset.List, {
		field: 'lostReason.tagId',
		title: 'order.lostReason',
		searchField: 'value',
		displayText: 'value',
		dataPromise: async () => {
			const filter = new RequestBuilder();
			filter.addFilter({ field: 'type' }, comparisonTypes.Equals, 'orderlostreason');
			const { metadata, data } = await FieldTranslations.find(filter.build());
			return {
				metadata,
				data: data.map(({ tagId, value }: { tagId: number; value: string }) => ({ id: tagId, value }))
			};
		},
		parent: 'filter.orderOther'
	}),
	...generateStandardAndHiddenFilter('Competitor', FilterPreset.List, {
		field: 'competitorId',
		title: 'order.competitorId',
		dataPromise: () => Promise.resolve(Competitor.find()),
		parent: 'filter.orderOther'
	}),
	...generateStandardAndHiddenFilter('ClientConnection', FilterPreset.Text, {
		title: 'default.clientConnection',
		field: 'clientConnection.name',
		parent: 'filter.orderOther'
	}),

	Age: {
		title: 'default.age',
		type: 'raw',
		hide: true,
		generate: function () {
			return { value: { value: null, comp: 'gte' } };
		},
		build: function (filter, rb, getField) {
			const value = filter.value?.value;
			const comp = filter.value?.comp;
			if (value !== undefined && value !== null) {
				const date = moment().utc().subtract(value, 'day').format();
				rb.addFilter(
					getField('regDate'),
					comp === 'lte' ? comparisonTypes.LessThanEquals : comparisonTypes.GreaterThanEquals,
					date
				);
			}
		}
	} as FilterConfig,

	// Quick search
	ListSearch: generateFilter(
		FilterPreset.ListSearch,
		{
			build: (filter, rb) => {
				if (filter.value?.length) {
					const orBuilder = rb.orBuilder();

					orBuilder.next();
					orBuilder.addFilter({ field: 'client.name' }, comparisonTypes.Wildcard, filter.value);

					orBuilder.next();
					orBuilder.addFilter({ field: 'contact.name' }, comparisonTypes.Wildcard, filter.value);

					orBuilder.next();
					orBuilder.addFilter({ field: 'orderRow.product.name' }, comparisonTypes.Wildcard, filter.value);

					orBuilder.next();
					orBuilder.addFilter({ field: 'user.name' }, comparisonTypes.Wildcard, filter.value);

					orBuilder.next();
					orBuilder.addFilter({ field: 'description' }, comparisonTypes.Wildcard, filter.value);

					if (Number.isInteger(Number(filter.value))) {
						orBuilder.next();
						orBuilder.addFilter({ field: 'id' }, comparisonTypes.Equals, Number(filter.value));
					}

					orBuilder.done();
				}
			}
		},
		[]
	)
});

export default orderFilterConfigs;
